import { RestaurantTable } from '@/api/tableApi';
import useTables from '@/hooks/tables/useTables';
import { selectActiveStore } from '@/redux/auth/authSlice';
import { useAppSelector } from '@/redux/hooks';
import {
  CheckOutlined,
  LockOutlined,
  SearchOutlined,
  StarOutlined,
  TableOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Badge, Input, Modal, Skeleton, Tooltip, Typography } from 'antd';
import i18n from 'i18next';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

const { Text, Title } = Typography;

interface TableSelectorModalProps {
  open: boolean;
  onSelect: (tableId: string) => void;
  onCancel: () => void;
  selectedTableId?: string;
}

const TableSelectorModal = ({
  open,
  onSelect,
  onCancel,
  selectedTableId,
}: TableSelectorModalProps) => {
  const { t } = useTranslation();
  const activeStore = useAppSelector(selectActiveStore);
  const { tables, tablesLoading } = useTables(undefined, activeStore?._id);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<
    'ALL' | 'AVAILABLE' | 'OCCUPIED' | 'RESERVED' | 'CLEANING'
  >('ALL');

  const getTableStatusConfig = useCallback(
    (status: RestaurantTable['status']) => {
      const configs = {
        AVAILABLE: {
          bg: 'bg-gradient-to-br from-emerald-50 to-emerald-100',
          border: 'border-emerald-300 hover:border-emerald-400',
          text: 'text-emerald-700',
          hover: 'hover:shadow-lg hover:shadow-emerald-200',
          badge: 'bg-emerald-500',
          icon: '✓',
          label: t('common:available'),
        },
        OCCUPIED: {
          bg: 'bg-gradient-to-br from-rose-50 to-rose-100',
          border: 'border-rose-300 hover:border-rose-400',
          text: 'text-rose-700',
          hover: '',
          badge: 'bg-rose-500',
          icon: '●',
          label: t('common:occupied'),
        },
        RESERVED: {
          bg: 'bg-gradient-to-br from-amber-50 to-amber-100',
          border: 'border-amber-300 hover:border-amber-400',
          text: 'text-amber-700',
          hover: 'hover:shadow-lg hover:shadow-amber-200',
          badge: 'bg-amber-500',
          icon: '⭐',
          label: t('common:reserved'),
        },
        CLEANING: {
          bg: 'bg-gradient-to-br from-slate-50 to-slate-100',
          border: 'border-slate-300 hover:border-slate-400',
          text: 'text-slate-600',
          hover: '',
          badge: 'bg-slate-500',
          icon: '🔄',
          label: t('common:cleaning'),
        },
      };
      return configs[status] || configs.AVAILABLE;
    },
    [t]
  );

  const filteredAndSortedTables = useMemo(() => {
    if (!tables) return [];

    const filtered = tables.filter((table) => {
      if (table.enabled === false) return false;

      const matchesSearch =
        !searchQuery ||
        table.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        table.number.toString().includes(searchQuery) ||
        (table.nameAr && table.nameAr.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (table.category?.name &&
          table.category.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (table.category?.nameAr &&
          table.category.nameAr.toLowerCase().includes(searchQuery.toLowerCase()));

      const matchesStatus = statusFilter === 'ALL' || table.status === statusFilter;

      return matchesSearch && matchesStatus;
    });

    return filtered.sort((a, b) => {
      if (a.displayOrder !== b.displayOrder) {
        return (a.displayOrder || 0) - (b.displayOrder || 0);
      }
      return a.number - b.number;
    });
  }, [tables, searchQuery, statusFilter]);

  const tablesByCategory = useMemo(() => {
    if (!filteredAndSortedTables.length) return [];

    const grouped = filteredAndSortedTables.reduce(
      (acc, table) => {
        const categoryId = table.categoryId || 'uncategorized';
        const categoryName = table.category
          ? i18n.language === 'ar' && table.category.nameAr
            ? table.category.nameAr
            : table.category.name
          : t('common:no_category');

        if (!acc[categoryId]) {
          acc[categoryId] = {
            id: categoryId,
            name: categoryName,
            color: table.category?.color || '#9CA3AF',
            displayOrder: table.category?.displayOrder ?? 9999,
            tables: [],
          };
        }
        acc[categoryId].tables.push(table);
        return acc;
      },
      {} as Record<
        string,
        {
          id: string;
          name: string;
          color: string;
          displayOrder: number;
          tables: RestaurantTable[];
        }
      >
    );

    return Object.values(grouped).sort((a, b) => {
      if (a.displayOrder !== b.displayOrder) {
        return a.displayOrder - b.displayOrder;
      }
      return a.name.localeCompare(b.name);
    });
  }, [filteredAndSortedTables, t]);

  const availableTablesCount = useMemo(
    () =>
      tables?.filter((table) => table.status === 'AVAILABLE' && table.enabled !== false).length ||
      0,
    [tables]
  );

  const handleTableClick = useCallback(
    (table: RestaurantTable) => {
      if (table.status === 'AVAILABLE' && table.enabled !== false) {
        onSelect(table._id);
      }
    },
    [onSelect]
  );

  const statusFilters = [
    { key: 'ALL', label: t('common:all'), count: tables?.length || 0 },
    {
      key: 'AVAILABLE',
      label: t('common:available'),
      count: availableTablesCount,
    },
    {
      key: 'OCCUPIED',
      label: t('common:occupied'),
      count: tables?.filter((t) => t.status === 'OCCUPIED' && t.enabled !== false).length || 0,
    },
    {
      key: 'RESERVED',
      label: t('common:reserved'),
      count: tables?.filter((t) => t.status === 'RESERVED' && t.enabled !== false).length || 0,
    },
    {
      key: 'CLEANING',
      label: t('common:cleaning'),
      count: tables?.filter((t) => t.status === 'CLEANING' && t.enabled !== false).length || 0,
    },
  ];

  return (
    <Modal
      title={
        <div className="flex items-center gap-2">
          <TableOutlined className="text-[#17527B]" />
          <Title level={4} className="!mb-0">
            {t('common:select_table')}
          </Title>
        </div>
      }
      open={open}
      onCancel={onCancel}
      footer={null}
      centered
      width={800}
      destroyOnClose
      className="table-selector-modal"
      style={{ maxHeight: '90vh' }}
      styles={{
        body: {
          maxHeight: 'calc(90vh - 110px)',
          overflow: 'hidden',
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <div className="flex flex-col" style={{ minHeight: 0, flex: 1 }}>
        {tablesLoading ? (
          <div className="space-y-4 flex-1 overflow-y-auto">
            <Skeleton active paragraph={{ rows: 2 }} />
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {Array.from({ length: 10 }).map((_, i) => (
                <Skeleton.Button key={i} active block className="h-[120px]" />
              ))}
            </div>
          </div>
        ) : !tables || tables.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 flex-1">
            <div className="text-6xl mb-4 opacity-30">📭</div>
            <Text strong className="text-base mb-2">
              {t('common:no_tables_found')}
            </Text>
            <Text type="secondary" className="text-sm">
              {t('common:no_data')}
            </Text>
          </div>
        ) : (
          <>
            {/* Search */}
            <div className="mb-2 flex-shrink-0">
              <Input
                placeholder={t('common:search_tables')}
                prefix={<SearchOutlined className="text-gray-400" />}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="middle"
                allowClear
                className="rounded-lg shadow-sm"
              />
            </div>

            {/* Status Filters */}
            <div className="mb-3 flex items-center gap-1.5 flex-wrap flex-shrink-0">
              {statusFilters.map((filter) => {
                const isActive = statusFilter === filter.key;
                return (
                  <Tooltip
                    key={filter.key}
                    title={`${filter.count} ${filter.label}`}
                    placement="top"
                  >
                    <button
                      onClick={() =>
                        setStatusFilter(
                          filter.key as 'ALL' | 'AVAILABLE' | 'OCCUPIED' | 'RESERVED' | 'CLEANING'
                        )
                      }
                      className={`
                        px-3 py-1.5 rounded-lg font-medium text-xs transition-all duration-300 border-none
                        ${
                          isActive
                            ? 'bg-[#17527B] text-white shadow-md scale-105'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-sm'
                        }
                      `}
                    >
                      <span className="flex items-center gap-2">
                        {filter.label}
                        {filter.count > 0 && (
                          <Badge
                            count={filter.count}
                            style={{
                              backgroundColor: isActive ? '#ffffff' : '#17527B',
                              color: isActive ? '#17527B' : '#ffffff',
                              fontSize: '10px',
                              height: '20px',
                              lineHeight: '20px',
                            }}
                          />
                        )}
                      </span>
                    </button>
                  </Tooltip>
                );
              })}
            </div>

            {/* Tables by Category */}
            {filteredAndSortedTables.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-4xl mb-3 opacity-25">🔍</div>
                <Text strong className="text-base block mb-1">
                  {t('common:no_tables_match_filter')}
                </Text>
                <Text type="secondary" className="text-sm">
                  {t('common:try_different_search')}
                </Text>
              </div>
            ) : (
              <div
                className="overflow-y-auto scrollable pr-1 space-y-6"
                style={{ maxHeight: 'calc(90vh - 280px)' }}
              >
                {tablesByCategory.map((category) => (
                  <div key={category.id} className="space-y-3">
                    {/* Category Header */}
                    <div
                      className="flex items-center gap-3 px-2 py-2 rounded-lg sticky top-0 z-10 backdrop-blur-sm bg-white/95 shadow-sm"
                      style={{ borderLeft: `4px solid ${category.color}` }}
                    >
                      <div
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: category.color }}
                      />
                      <Text strong className="text-base" style={{ color: category.color }}>
                        {category.name}
                      </Text>
                      <Badge
                        count={category.tables.length}
                        style={{
                          backgroundColor: category.color,
                          fontSize: '11px',
                          height: '20px',
                          lineHeight: '20px',
                        }}
                      />
                    </div>

                    {/* Tables Grid for this Category */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 px-2">
                      {category.tables.map((table) => {
                        const isSelected = selectedTableId === table._id;
                        const isAvailable = table.status === 'AVAILABLE';
                        const isClickable = isAvailable;
                        const statusConfig = getTableStatusConfig(table.status);
                        const hasActiveOrders =
                          table.hasActiveOrders && table.activeOrders?.length > 0;

                        return (
                          <Tooltip
                            key={table._id}
                            title={
                              isClickable
                                ? `${t('common:click_to_select')} ${table.name || `Table ${table.number}`}`
                                : statusConfig.label
                            }
                            placement="top"
                          >
                            <button
                              onClick={() => handleTableClick(table)}
                              disabled={!isClickable}
                              className={`
                                group relative p-4 rounded-2xl border-2 transition-all duration-300
                                ${statusConfig.bg}
                                ${statusConfig.border}
                                ${statusConfig.text}
                                ${isClickable ? `${statusConfig.hover} cursor-pointer` : 'opacity-50 cursor-not-allowed'}
                                ${isSelected ? 'ring-4 ring-blue-400 ring-offset-2 shadow-xl scale-105 z-10' : ''}
                                flex flex-col items-center justify-center gap-1.5
                                min-h-[110px] focus:outline-none focus:ring-2 focus:ring-offset-1
                              `}
                              style={{
                                borderTopColor: category.color,
                                borderTopWidth: '3px',
                              }}
                            >
                              {/* Selected Checkmark */}
                              {isSelected && (
                                <div className="absolute -top-3 -right-3 bg-blue-500 rounded-full p-1.5 shadow-lg z-20 animate-pulse">
                                  <CheckOutlined className="text-white text-base" />
                                </div>
                              )}

                              {/* Status Indicator */}
                              <div
                                className={`
                                  absolute top-2 left-2 ${statusConfig.badge} text-white text-[9px] 
                                  px-2 py-1 rounded-full font-semibold flex items-center gap-1
                                `}
                              >
                                <span>{statusConfig.icon}</span>
                                <span className="hidden sm:inline">{statusConfig.label}</span>
                              </div>

                              {/* Category Color Indicator */}
                              <div
                                className="absolute top-2 right-2 w-2 h-2 rounded-full"
                                style={{ backgroundColor: category.color }}
                              />

                              {/* Table Number */}
                              <Text
                                className={`
                                  font-bold text-xl mt-1 transition-colors
                                  ${isSelected ? 'text-blue-700' : statusConfig.text}
                                `}
                              >
                                {table.number}
                              </Text>

                              {/* Table Name */}
                              {table.name && (
                                <Text className="text-xs truncate max-w-[90px] opacity-70">
                                  {table.name}
                                </Text>
                              )}

                              {/* Capacity Indicator */}
                              {table.capacity > 0 && (
                                <div className="flex items-center gap-1 mt-auto text-xs opacity-75">
                                  <UserOutlined className="text-xs" />
                                  <span>{table.capacity}</span>
                                </div>
                              )}

                              {/* Active Orders Count */}
                              {hasActiveOrders && (
                                <div className="absolute bottom-2 right-2">
                                  <Badge
                                    count={table.activeOrders.length}
                                    style={{
                                      backgroundColor: '#17527B',
                                      fontSize: '10px',
                                      height: '22px',
                                      lineHeight: '22px',
                                    }}
                                  />
                                </div>
                              )}
                            </button>
                          </Tooltip>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Summary Stats */}
            {availableTablesCount > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-2 gap-4 flex-shrink-0">
                <div className="flex items-center gap-2">
                  <StarOutlined className="text-emerald-500 text-lg" />
                  <Text type="secondary" className="text-sm">
                    <strong className="text-emerald-700">{availableTablesCount}</strong>{' '}
                    {t('common:available')}
                  </Text>
                </div>
                <div className="flex items-center gap-2">
                  <LockOutlined className="text-rose-500 text-lg" />
                  <Text type="secondary" className="text-sm">
                    <strong className="text-rose-700">
                      {
                        (
                          tables?.filter((t) => t.status === 'OCCUPIED' && t.enabled !== false) ||
                          []
                        ).length
                      }
                    </strong>{' '}
                    {t('common:occupied')}
                  </Text>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};

export default TableSelectorModal;
